import React, { useState, useRef } from "react";
import { Mic, Loader2, ArrowRight, Check } from "lucide-react";
import { onboard, transcribe } from "../../lib/api";
import { ARIA } from "../../lib/assets";

const STEPS = [
  { key: "name_role", q: "First, what should I call you, and what's your role?", map: (v) => ({ name: v.split(",")[0]?.trim(), role: v }) },
  { key: "organization", q: "What organization or ventures are you leading?", map: (v) => ({ organization: v }) },
  { key: "priorities", q: "What are your top 2–3 priorities right now?", map: (v) => ({ priorities: v }) },
  { key: "key_people", q: "Who are the most important people I should keep track of for you?", map: (v) => ({ key_people: v }) },
  { key: "working_style", q: "Lastly — how do you like to work and be communicated with?", map: (v) => ({ working_style: v, comms_style: v }) },
];

export default function Onboarding({ onDone }) {
  const [step, setStep] = useState(0);
  const [input, setInput] = useState("");
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recBusy, setRecBusy] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const next = async () => {
    const val = input.trim();
    const merged = { ...answers, ...(val ? current.map(val) : {}) };
    setAnswers(merged);
    setInput("");
    if (isLast) {
      setSaving(true);
      try { await onboard(merged); } catch (e) {}
      setSaving(false);
      onDone();
    } else {
      setStep(step + 1);
    }
  };

  const skip = async () => {
    setSaving(true);
    try { await onboard({ ...answers, onboarded: true }); } catch (e) {}
    setSaving(false);
    onDone();
  };

  const toggleMic = async () => {
    if (recording) { mediaRef.current && mediaRef.current.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false); setRecBusy(true);
        try {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          const text = await transcribe(blob, "");
          if (text) setInput((p) => (p ? p + " " : "") + text);
        } catch (e) {}
        setRecBusy(false);
      };
      mediaRef.current = mr; mr.start(); setRecording(true);
    } catch (e) {}
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#0A0908]/95 backdrop-blur-xl flex items-center justify-center p-5" data-testid="onboarding-overlay">
      <div className="max-w-lg w-full text-center">
        <div className="flex justify-center mb-6">
          <img src={ARIA.portrait} alt="Aria" className="w-20 h-20 rounded-full object-cover border border-amber-400/40 shadow-[0_0_40px_-8px_rgba(226,184,87,0.7)]" />
        </div>
        <div className="overline mb-2">Let's make me yours · {step + 1} of {STEPS.length}</div>
        <h2 className="font-display text-3xl sm:text-4xl font-light text-amber-50 leading-tight fade-up" key={step}>
          {current.q}
        </h2>

        <div className="mt-8 flex items-end gap-2 glass rounded-2xl p-2.5">
          <button onClick={toggleMic} className={`rounded-xl p-3 transition ${recording ? "text-rose-400 mic-live" : "text-amber-300/90 hover:bg-amber-500/10"}`} data-testid="onboarding-mic">
            {recBusy ? <Loader2 size={18} className="animate-spin" /> : <Mic size={18} />}
          </button>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); next(); } }}
            rows={1}
            autoFocus
            placeholder="Type or speak your answer…"
            className="flex-1 bg-transparent resize-none px-2 py-2.5 text-[15px] focus:outline-none max-h-32 text-left"
            data-testid="onboarding-input"
          />
          <button onClick={next} disabled={saving} className="gold-btn rounded-xl p-3" data-testid="onboarding-next">
            {saving ? <Loader2 size={18} className="animate-spin" /> : isLast ? <Check size={18} /> : <ArrowRight size={18} />}
          </button>
        </div>

        <div className="mt-5 flex items-center justify-center gap-4 text-[12px]">
          <button onClick={next} className="text-slate-400 hover:text-amber-300 transition" data-testid="onboarding-skip-question">Skip this question</button>
          <span className="text-slate-600">·</span>
          <button onClick={skip} className="text-slate-400 hover:text-amber-300 transition" data-testid="onboarding-skip-all">Skip onboarding</button>
        </div>
      </div>
    </div>
  );
}
